import React, {
  useState,
  useEffect,
  SyntheticEvent,
  memo,
  useCallback,
} from 'react';
import {
  Button,
  CenteredModal,
  Input,
  SubmitButton,
  TextArea,
  useBoolModalState,
} from 'components';
import { useTranslation, Trans } from 'react-i18next';
import { createUseStyles, useTheme } from 'react-jss';

import { useToast, Loader } from 'components';
import { SessionItem } from '../../Sessions/components/SessionItem';
import illustration from '@app/illu/Image/EmptySessions.svg';
import { Theme } from '@app/theme';
import {
  useLiveSessions,
  useCurrentOrganizationId,
  useIsAdmin,
  useBulkInvite,
} from '../../../../queries';
import { Form, Formik } from 'formik';

interface Props {
  participants: string[];
  isOpen: boolean;
  onRequestClose: () => any;
  onInviteSuccess: () => any;
}

const useStyles = createUseStyles((theme: Theme) => ({
  sessionItem: {
    marginTop: theme.marginBase * 2,
  },
  emptyContainer: {
    backgroundColor: theme.colors.grayBackground,
    padding: theme.marginBase * 4,
    textAlign: 'center',
  },
  emptyTitle: {
    ...theme.fonts.h5,
    textAlign: 'center',
  },
  illustration: {
    width: '100%',
    maxWidth: 380,
    marginBottom: theme.marginBase * 3,
  },
}));

export const InviteParticipantsModal = memo((props: Props) => {
  const { participants, onRequestClose, isOpen } = props;
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const toast = useToast();
  const { mutateAsync: bulkInvite } = useBulkInvite();

  const [selected, setSelected] = useState<string | undefined>(undefined);
  const [submiting, setSubmiting] = useState(false);
  const [messageModalOpened, openMessageModal, closeMessageModal] =
    useBoolModalState();

  const isAdmin = useIsAdmin();

  const organizationId = useCurrentOrganizationId();
  const { data: sessions = [], isLoading: fetching } =
    useLiveSessions(organizationId);

  useEffect(() => {
    setSelected(undefined);
    setSubmiting(false);
  }, [setSelected, isOpen]);

  const onSendWithMessage = useCallback(
    async (values: { message: string; subject: string }) => {
      if (!selected) {
        return;
      }
      const count = await bulkInvite({
        participantIds: participants,
        sessionId: selected,
        message: values.message || undefined,
        subject: values.subject || undefined,
      });
      toast.success(t('toast.invited', { count }));
      onRequestClose();
      closeMessageModal();
    },
    [
      selected,
      participants,
      bulkInvite,
      onRequestClose,
      closeMessageModal,
      t,
      toast,
    ],
  );

  const onSend = async () => {
    try {
      if (!selected) {
        return;
      }
      setSubmiting(true);
      const count = await bulkInvite({
        participantIds: participants,
        sessionId: selected,
      });
      toast.success(t('toast.invited', { count }));
      props.onInviteSuccess();
      onRequestClose();
    } catch (e) {
      toast.exception(e);
    }
    setSubmiting(false);
  };

  return (
    <React.Fragment>
      <CenteredModal
        isOpen={isOpen && !messageModalOpened}
        onRequestClose={onRequestClose}
        title={t('participants.list.invite.title', {
          count: participants.length,
        })}
        id="invite_participant"
        buttons={[
          <Button
            onClick={openMessageModal}
            color="blue"
            small
            key="invite-subject"
            disabled={!selected}
            text={t('participants.list.invite.inviteWithMessage', {
              count: participants.length,
            })}
          />,
          <Button
            onClick={onSend}
            color="green"
            small
            key="invite"
            disabled={!selected}
            text={t('participants.list.invite.sessionConfirm', {
              count: participants.length,
            })}
            isLoading={submiting}
          />,
        ]}
      >
        {fetching && !sessions.length && <Loader />}
        {!fetching && !sessions.length && (
          <div className={classes.emptyContainer}>
            <img
              alt="illustrations"
              src={illustration}
              className={classes.illustration}
            />
            <h4 className={classes.emptyTitle}>
              <Trans i18nKey="participants.list.invite.empty" />
            </h4>
          </div>
        )}
        {isAdmin && (
          <Input
            value={selected}
            onChange={(e: SyntheticEvent<HTMLInputElement>) =>
              setSelected(e.currentTarget.value)
            }
            label="Session id"
          />
        )}
        {sessions.map((session) => (
          <SessionItem
            session={session}
            key={session.id}
            className={classes.sessionItem}
            small
            overWhite
            noStatus
            selected={selected === session.id}
            onClick={() => setSelected(session.id)}
          />
        ))}
      </CenteredModal>
      <Formik
        initialValues={{ message: '', subject: '' }}
        onSubmit={onSendWithMessage}
      >
        {(formik) => (
          <CenteredModal
            isOpen={isOpen && messageModalOpened}
            onRequestClose={closeMessageModal}
            title={t('participants.list.invite.messageTitle', {
              count: participants.length,
            })}
            id="invite_participant"
            buttons={
              <SubmitButton
                onClick={() => formik.handleSubmit()}
                color="green"
                small
                text={t('participants.list.invite.sessionConfirm', {
                  count: participants.length,
                })}
                isLoading={formik.isSubmitting}
              />
            }
          >
            <Form>
              <Input
                name="subject"
                label={t('participants.list.invite.subject')}
              />
              <TextArea
                name="message"
                label={t('participants.list.invite.message')}
              />
            </Form>
          </CenteredModal>
        )}
      </Formik>
    </React.Fragment>
  );
});
